import { useContext, useMemo } from 'react'
import { DesignContext } from 'contexts/DesignContext'
import { ConfigContext } from 'contexts/ConfigContext'
import objectPath from 'object-path'
import { Form, Col } from 'react-bootstrap'
import FavoriteButton from './FavoriteButton'
import { isNumber, toMetric } from '../action/Data'
import Icon from './Icon'
import { isEmpty } from '../action/condition/State'
import NumberFormat from 'react-number-format'
import { useTimeFormat } from 'contexts/TimeFormatContext'
import { onCheckCondition } from '../action/condition/Action'
import moment from 'moment'
import { RequireLabel, RequireToggle } from 'components/RequireConfig'

const LabelValue = ({
  uischema,
  fieldName,
  format,
  value,
  formatDate,
  formatTime,
  formatDateTime,
}) => {
  if (uischema.tagname === 'label') {
    if (format === 'currency') {
      return (
        <NumberFormat
          name={fieldName}
          thousandSeparator
          value={parseFloat(Number(value).toFixed(2))}
          displayType="text"
        />
      )
    }
    if (format === 'metric') {
      return toMetric(value)
    }
    if (format === 'date') {
      return formatDate(value)
    }
    if (format === 'time') {
      return formatTime(value)
    }
    if (format === 'dateTime') {
      return formatDateTime(value)
    }
    if (format === 'fromNow') {
      if (value) {
        return moment(value).fromNow()
      }
      return ''
    }
  }

  return value
}

const Label = (props) => {
  const {
    uischema,
    label,
    mt = '1',
    style = {},
    isRequire = false,
    isEnable,
    onClick = () => {},
    config = {},
    checkboxTextSize,
    value,
    fieldName,
    fieldKey,
    isShowIcon = false,
    requireField,
  } = props
  let { isDynamic } = config
  const { formatDate, formatTime, formatDateTime } = useTimeFormat()
  const designContext = useContext(DesignContext)
  const configContext = useContext(ConfigContext)
  const { layoutType } = configContext.state
  const { jsonForm, isUiChanged } = designContext
  let labelValue = isDynamic
    ? value
    : value || objectPath.get(jsonForm.formData, uischema.key)
  let prefixLabel = uischema.prefixLabel ? uischema.prefixLabel + ' ' : ''
  let suffixLabel = uischema.suffixLabel ? ' ' + uischema.suffixLabel : ''
  let format = uischema?.format || 'text'
  let uiLabel =
    uischema.tagname === 'label'
      ? isNumber(labelValue)
        ? labelValue
        : labelValue || label || uischema?.label
      : label || uischema?.label

  const { fieldLabelColor } = uischema

  const colorFromField = fieldLabelColor
    ? objectPath.get(jsonForm.formData, fieldLabelColor)
    : uischema.labelColor

  const colorByTheme = useMemo(() => {
    let color =
      layoutType === 'dark'
        ? '#adb7be'
        : fieldLabelColor
        ? colorFromField
        : uischema.labelColor
    return color
  }, [uischema.key, colorFromField])

  const styleDefault = {
    justifyContent: uischema?.textAlign || 'unset',
    fontSize: uischema?.fontSize ?? '14px',
    fontWeight: uischema?.fontWeight ?? 'normal',
    color: colorByTheme,
  }
  const styleWarning = {
    ...styleDefault,
    padding:
      uischema.warningColor === 'rgba(255, 255, 255, 1)'
        ? ''
        : '0.25rem 0.5rem',
    width: 'max-content',
    background: uischema.warningColor,
    color: uischema.labelColor,
    borderRadius: '0.25rem',
  }

  const checkFavorite = objectPath.get(uischema, 'favorite.enabled')

  const className =
    uischema.tagname !== 'label' && isEmpty(uiLabel)
      ? 'px-0'
      : props.className || 'd-flex align-items-center'
  const isChangeColor = objectPath.get(
    isUiChanged,
    `isChangeColor.${uischema.key}`,
  )
  const labelStyle = useMemo(() => {
    if (isChangeColor) {
      const { labelColor } = isChangeColor
      return {
        ...styleDefault,
        color: labelColor,
        padding: 0,
      }
    }
    if (uischema?.labelWarning) {
      return styleWarning
    }
    return styleDefault
  }, [
    uiLabel,
    isChangeColor,
    uischema.labelColor,
    uischema.warningColor,
    uischema?.fontWeight,
  ])
  const labelWidth = checkboxTextSize || uischema?.labelWidth
  const isCustomLabelWidth = !checkboxTextSize && labelWidth === 'custom'
  const labelWidthStyle =
    isCustomLabelWidth && uischema?.labelCustomWidth
      ? {
          flex: `0 0 ${uischema.labelCustomWidth}`,
          maxWidth: uischema.labelCustomWidth,
          width: uischema.labelCustomWidth,
        }
      : {}

  return (
    <Col
      className={className ?? `mt-${mt} px-0 mx-0`}
      sm={isCustomLabelWidth ? 'auto' : labelWidth}
      data-designer-preview-label-box={uischema.key}
      style={{
        ...labelWidthStyle,
        padding: uischema?.labelPadding ?? '',
        margin: uischema?.labelMargin ?? '',
      }}
    >
      {!checkboxTextSize && <RequireToggle field={requireField} />}
      <Form.Label
        id={`label-${fieldKey || uischema.key}`}
        name={`label-${fieldName || uischema.name}`}
        className={`m-0 w-100 d-flex`}
        style={{ ...style, justifyContent: uischema?.textAlign || 'unset' }}
        onClick={() => {
          onCheckCondition(designContext, jsonForm.formData, uischema.key, {
            isDebounce: false,
            isOnClick: true,
          })
        }}
      >
        <div
          data-designer-preview-label-content={uischema.key}
          className={`d-flex ${checkFavorite ? 'align-items-center' : ''}`}
          style={labelStyle}
        >
          <div onClick={onClick}>
            {isShowIcon && uischema.prependIcon && (
              <Icon iconName={uischema.prependIcon} />
            )}{' '}
            <span data-designer-preview-label-prefix={uischema.key}>
              {prefixLabel}
            </span>
            <span data-designer-preview-label-value={uischema.key}>
              <LabelValue
                uischema={uischema}
                fieldName={fieldName}
                value={uiLabel}
                format={format}
                formatDate={formatDate}
                formatTime={formatTime}
                formatDateTime={formatDateTime}
              />
            </span>
            <span data-designer-preview-label-suffix={uischema.key}>
              {suffixLabel}
            </span>
            {isShowIcon && uischema.appendIcon && (
              <Icon iconName={uischema.appendIcon} />
            )}
            <RequireLabel required={Boolean(uiLabel && isRequire)} />
          </div>
          {uiLabel && isEnable && !config.isFavoriteModal ? (
            <FavoriteButton uischema={uischema} />
          ) : (
            ''
          )}
        </div>
      </Form.Label>
    </Col>
  )
}

export default Label
